import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import CountUp from "react-countup";
import logo from "../assets/EditedPng.png";

export default function Home({
  homeData = {
    mainHeading: "Building Dreams Into Reality",
    tagLine: "Excellence in every brick",
    projectCount: 150,
    clientsCount: 120,
    experience: 15
  }
}) {
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    setPrefersReducedMotion(mediaQuery.matches);
    const handler = (e) => setPrefersReducedMotion(e.matches);
    mediaQuery.addEventListener('change', handler);
    return () => mediaQuery.removeEventListener('change', handler);
  }, []);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const disableMotion = prefersReducedMotion || isMobile;


  const containerVariants = disableMotion ? {
    hidden: { opacity: 1 },
    visible: { opacity: 1 }
  } : {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.08,
        delayChildren: 0
      }
    }
  };

  const itemVariants = disableMotion ? {
    hidden: { opacity: 1 },
    visible: { opacity: 1 }
  } : {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: "spring",
        stiffness: 100,
        damping: 20
      }
    }
  };

  return (
    <section id="home" className="relative flex items-center justify-center min-h-[100svh] pt-20 md:pt-20 pb-12 sm:w-full">
      <div className="container mx-auto px-6 lg:px-12 max-w-5xl">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={disableMotion ? "visible" : "visible"}
          className="space-y-6"
        >
          {/* Logo & Brand Name */}
          <motion.div
            variants={itemVariants}
            className="flex flex-col md:flex-row items-center gap-6 md:gap-8 mb-6 md:mb-8"
          >
            <motion.div
              className="relative bg-white/5 backdrop-blur-md p-6 rounded-xl border border-white/10 hover:shadow-2xl transition-all duration-300"
              whileHover={disableMotion ? {} : { y: -5 }}
              whileTap={disableMotion ? {} : { scale: 0.98 }}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: disableMotion ? 0 : 0.4 }}
              viewport={{ once: true }}
            >
              <motion.div
                className="absolute inset-0 rounded-xl border-2 border-transparent"
                animate={
                  disableMotion
                    ? {}
                    : {
                      borderColor: [
                        "rgba(255,215,0,0.3)",
                        "rgba(255,215,0,0.8)",
                        "rgba(255,215,0,0.3)",
                      ],
                    }
                }
                transition={{
                  duration: 2,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
                style={{
                  background: "linear-gradient(205deg, #d6d3d3ff 0%, #ffffffff 60%, #dddbdbff 100%)",
                }}
              />
              <motion.img
                src={logo}
                alt="Nest Constructions Logo"
                className="w-24 h-24 md:w-40 md:h-40 object-contain filter drop-shadow-2xl relative z-10"
                initial={disableMotion ? false : { opacity: 0 }}
                animate={disableMotion ? {} : { opacity: 1 }}
                transition={disableMotion ? {} : { duration: 0.4 }}
              />
            </motion.div>
            <div className="flex flex-col justify-center items-center md:items-start text-center md:text-left">
              <div className="text-secondary font-heading font-medium text-sm md:text-base tracking-[0.4em] leading-none mb-1 ml-1">
                KERALA
              </div>
              <div className="flex flex-col">
                <span className="text-white font-heading font-semibold text-4xl md:text-5xl lg:text-6xl tracking-widest leading-none">
                  NEST
                </span>
                <span className="text-white font-heading font-medium text-2xl md:text-3xl lg:text-4xl tracking-wide leading-none">
                  CONSTRUCTIONS
                </span>
                <span className="text-white font-heading font-medium text-xl md:text-xl lg:text-xl tracking-wide leading-none">
                  (OPC) PRIVATE LIMITED
                </span>
              </div>
            </div>
          </motion.div>

          {/* Main Heading */}
          <motion.h1
            variants={itemVariants}
            className="text-4xl md:text-5xl lg:text-6xl font-heading font-semibold tracking-tight text-white leading-tight text-center md:text-left drop-shadow-lg"
          >
            {homeData.mainHeading || "Building Dreams Into Reality"}
          </motion.h1>

          {/* Tagline */}
          <motion.div
            variants={itemVariants}
            className="mb-8 max-w-2xl text-center md:text-left mx-auto md:mx-0"
          >
            <p className="text-lg md:text-xl text-white md:text-slate-200 font-bold leading-relaxed relative inline-block drop-shadow-lg">
              <span className="text-secondary text-3xl md:text-4xl font-serif leading-none opacity-90 mr-2">"</span>
              {homeData.tagLine || "Excellence in every brick, creating spaces that inspire and endure for generations to come."}
              <span className="text-secondary text-3xl md:text-4xl font-serif leading-none opacity-90 ml-2">"</span>
            </p>
          </motion.div>

          {/* Actions & Stats Container */}
          <motion.div
            variants={itemVariants}
            className="flex flex-col md:flex-row items-center md:items-end justify-between gap-6 md:gap-8 w-full mt-0"
          >
            {/* Buttons */}
            <motion.div
              className="flex flex-wrap justify-center md:justify-start gap-4"
              variants={itemVariants}
            >
              <motion.a
                href="#projects"
                className="px-6 py-3 md:px-8 md:py-4 bg-secondary hover:bg-secondary-dark text-primary-dark font-bold rounded-lg shadow-lg shadow-secondary/20 transition-all transform hover:-translate-y-1 block text-sm md:text-base"
                whileHover={disableMotion ? {} : { scale: 1.02 }}
                whileTap={disableMotion ? {} : { scale: 0.98 }}
              >
                View Our Projects
              </motion.a>
              <motion.a
                href="#contact"
                className="px-6 py-3 md:px-8 md:py-4 bg-transparent border border-white/30 hover:border-white text-white font-semibold rounded-lg backdrop-blur-sm transition-all hover:bg-white/5 block text-sm md:text-base"
                whileHover={disableMotion ? {} : { scale: 1.02 }}
                whileTap={disableMotion ? {} : { scale: 0.98 }}
              >
                Contact Us
              </motion.a>
            </motion.div>

            {/* Stats Compact */}
            <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-xl p-3 md:p-8 flex gap-2 md:gap-10 shadow-lg w-full md:w-auto justify-around md:justify-start">
              <div className="flex flex-col items-center text-center flex-1 md:flex-none">
                <span className="text-xl md:text-4xl font-bold text-white font-heading">
                  <CountUp end={homeData.projectCount} duration={2.5} />+
                </span>
                <span className="text-slate-400 text-[10px] md:text-sm font-semibold uppercase tracking-wider">Projects</span>
              </div>
              <div className="w-px bg-white/10"></div>
              <div className="flex flex-col items-center text-center flex-1 md:flex-none">
                <span className="text-xl md:text-4xl font-bold text-white font-heading">
                  <CountUp end={homeData.clientsCount} duration={2.5} />+
                </span>
                <span className="text-slate-400 text-[10px] md:text-sm font-semibold uppercase tracking-wider">Clients</span>
              </div>
              <div className="w-px bg-white/10"></div>
              <div className="flex flex-col items-center text-center flex-1 md:flex-none">
                <span className="text-xl md:text-4xl font-bold text-white font-heading">
                  <CountUp end={homeData.experience} duration={2.5} />+
                </span>
                <span className="text-slate-400 text-[10px] md:text-sm font-semibold uppercase tracking-wider">Years</span>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
